import { useState, useCallback } from 'react'
import { useEvents, useEventStats, useEventsCalendar, useDeleteEvent } from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { SlotInfo } from 'react-big-calendar'
import { EventCalendar } from '@/components/EventCalendar'
import { CreateEventModal } from '@/components/CreateEventModal'
import { EditEventModal } from '@/components/EditEventModal'
import { EventDetailModal } from '@/components/EventDetailModal'
import { MobileEventList } from '@/components/MobileEventCard'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { useMobile } from '@/hooks/use-mobile'
import { useToast } from '@/hooks/use-toast'
import { exportEventsToCSV } from '@/lib/csv-export'
import {
  Calendar,
  Search,
  Plus,
  Clock,
  MapPin,
  User,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
  BarChart3,
  Download,
} from 'lucide-react'
import moment from 'moment'
import type { Event as EventType } from '@/types'

export function Events() {
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'events')
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [showMyEvents, setShowMyEvents] = usePersistentState(fk('showMy'), !isAdmin)
  const [viewMode, setViewMode] = usePersistentState<'calendar' | 'list'>(
    fk('viewMode'),
    'calendar'
  )
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [initialEventDate, setInitialEventDate] = useState<Date>()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<EventType | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [eventToEdit, setEventToEdit] = useState<EventType | null>(null)

  // Calendar date range for fetching events
  const [calendarRange, setCalendarRange] = useState(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { start, end }
  })

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const isMobile = useMobile()
  const { toast } = useToast()

  // Fetch data
  const { data: statsData, isLoading: statsLoading } = useEventStats()
  const { data: listData, isLoading: listLoading } = useEvents({
    search,
    page,
    limit: 25,
    owner_id: showMyEvents ? impersonatedUser?.id || currentUser?.id : undefined,
  })
  const { data: calendarEvents, isLoading: calendarLoading } = useEventsCalendar(
    calendarRange.start.toISOString(),
    calendarRange.end.toISOString(),
    showMyEvents ? impersonatedUser?.id || currentUser?.id : undefined
  )

  // Delete mutation
  const deleteMutation = useDeleteEvent()

  const {
    items: events,
    total,
    currentPage,
    limit: _limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(listData)

  // Handle calendar view range changes
  const handleRangeChange = useCallback((range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      // Week or day view - use first and last dates
      if (range.length >= 2) {
        const startDate = range[0]
        const endDate = range[range.length - 1]
        if (startDate && endDate) {
          setCalendarRange({
            start: startDate,
            end: endDate,
          })
        }
      }
    } else {
      // Month or agenda view
      setCalendarRange(range)
    }
  }, [])

  // Handle selecting an event
  const handleSelectEvent = useCallback((event: EventType) => {
    setSelectedEventId(event.id)
    setDetailModalOpen(true)
  }, [])

  // Handle clicking on empty slot in calendar
  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    setInitialEventDate(slotInfo.start)
    setCreateModalOpen(true)
  }, [])

  // Handle delete event
  const handleDelete = useCallback((event: EventType) => {
    setEventToDelete(event)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (!eventToDelete) return

    deleteMutation.mutate(eventToDelete.id, {
      onSuccess: () => {
        toast({
          title: 'Event deleted successfully',
        })
        setDeleteModalOpen(false)
        setEventToDelete(null)
      },
      onError: (error: Error) => {
        toast({
          title: 'Failed to delete event',
          description: error.message,
          variant: 'destructive',
        })
      },
    })
  }, [eventToDelete, deleteMutation, toast])

  // Handle edit event
  const handleEdit = useCallback((event: EventType) => {
    setEventToEdit(event)
    setEditModalOpen(true)
  }, [])

  // Stats cards
  const stats = [
    {
      title: 'Total Events',
      value: statsData?.total || 0,
      icon: Calendar,
      description: 'All time',
    },
    {
      title: 'Upcoming',
      value: statsData?.upcoming || 0,
      icon: Clock,
      description: 'Future events',
    },
    {
      title: 'This Week',
      value: statsData?.this_week || 0,
      icon: CalendarDays,
      description: 'Current week',
    },
    {
      title: 'This Month',
      value: statsData?.this_month || 0,
      icon: BarChart3,
      description: 'Current month',
    },
  ]

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'Meeting':
        return 'success'
      case 'Call':
        return 'warning'
      case 'Email':
        return 'secondary'
      default:
        return 'default'
    }
  }

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of events
    // In a production app, you might want to fetch all events from the API
    exportEventsToCSV(events)
    toast({
      title: 'Export Successful',
      description: `Exported ${events.length} events to CSV`,
    })
  }

  return (
    <main className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="event" />
            <h2 className="text-3xl font-bold tracking-tight">Events & Calendar</h2>
          </div>
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['events']} label="events" />
          {canExport && (
            <Button variant="outline" aria-label="Export events to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button onClick={() => setCreateModalOpen(true)} aria-label="Create new event">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Event
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-3.5 w-3.5 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3">
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-xl font-bold leading-none">
                    {stat.value.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground leading-tight">{stat.description}</p>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter and View Tabs */}
      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'calendar' | 'list')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <Tabs
              defaultValue="all"
              value={showMyEvents ? 'my' : 'all'}
              onValueChange={(value) => {
                setShowMyEvents(value === 'my')
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all events">
                  All Events
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my events">
                  My Events
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <TabsList>
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                List
              </TabsTrigger>
            </TabsList>
          </div>

          {viewMode === 'list' && (
            <div className="flex items-center space-x-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-[150px] lg:w-[250px]"
                aria-label="Search events"
              />
            </div>
          )}
        </div>

        {/* Calendar View */}
        <TabsContent value="calendar" className="mt-0">
          {isMobile ? (
            <MobileEventList
              events={calendarEvents || []}
              onClick={handleSelectEvent}
              onEdit={(event) => {
                setSelectedEventId(event.id)
                setDetailModalOpen(true)
              }}
              onDelete={handleDelete}
              groupByDate={true}
            />
          ) : (
            <EventCalendar
              events={calendarEvents || []}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onRangeChange={handleRangeChange}
              loading={calendarLoading}
            />
          )}
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>{showMyEvents ? 'My Events' : 'All Events'}</CardTitle>
              <CardDescription>
                {total > 0 ? `Showing ${events.length} of ${total} events` : 'No events found'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Related To</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={7}>
                          <Skeleton className="h-12 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No events found
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{event.subject}</span>
                            {event.is_all_day_event && (
                              <Badge variant="outline" className="w-fit mt-1 text-xs">
                                All Day
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getTypeColor(event.type) as any}>
                            {event.type || 'Event'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <div className="text-sm">
                              <div>{moment(event.start_date_time).format('MMM D, YYYY')}</div>
                              {!event.is_all_day_event && (
                                <div className="text-muted-foreground">
                                  {moment(event.start_date_time).format('h:mm A')} -{' '}
                                  {moment(event.end_date_time).format('h:mm A')}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm truncate max-w-[150px]">
                                {event.location}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{event.owner_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {event.who_name && (
                              <div className="truncate max-w-[150px]">{event.who_name}</div>
                            )}
                            {event.what_name && (
                              <div className="text-muted-foreground truncate max-w-[150px]">
                                {event.what_name}
                              </div>
                            )}
                            {!event.who_name && !event.what_name && '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedEventId(event.id)
                              setDetailModalOpen(true)
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={prevPage} disabled={!hasPrevPage}>
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" onClick={nextPage} disabled={!hasNextPage}>
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CreateEventModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        initialDate={initialEventDate}
      />
      <EventDetailModal
        eventId={selectedEventId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onEdit={handleEdit}
      />
      <EditEventModal
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open)
          if (!open) setEventToEdit(null)
        }}
        event={eventToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{eventToDelete?.subject}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
